import { FC } from 'react'
import { unstable_useBlocker as useBlocker, useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { IndicatorsList, TasksList, Widget } from '@factorialco/f0-react/dist/experimental'
import { format, isThisYear } from 'date-fns'
import { TasksInsightDocument, TasksInsightQuery } from 'generated/resources/graphql'

import { parseBackendDate } from 'modules/core/components/DateNavigator/dateFormatUtils'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

import './index.styles.css'
const formatBadgeDate = (date: Date) => {
  return format(date, isThisYear(date) ? 'MMM d' : 'MMM d, yyyy')
}

const formatTasks = (
  tasks: TasksInsightQuery['tasks']['tasksSummariesConnection']['nodes']['0']['lastTasks']
) => {
  return tasks.map((task) => {
    const dueDate = task.dueOn ? parseBackendDate(task.dueOn) : undefined
    const isPastDue = dueDate && dueDate < new Date()

    const badgeText = dueDate
      ? `${i18n.t('insights.widgets.tasks.due')} ${formatBadgeDate(dueDate)}`
      : undefined

    return {
      id: task.id,
      text: task.name,
      badge: badgeText
        ? {
            text: badgeText,
            isPastDue: !!isPastDue,
          }
        : undefined,
    }
  })
}

const prepareData = (data: TasksInsightQuery) => {
  const summary = data.tasks.tasksSummariesConnection.nodes?.[0]
  const tasksList = summary?.lastTasks || []

  const dueCount = summary?.dueCount ?? 0
  const overdueCount = summary?.overdueCount ?? 0
  const noDueCount = summary?.noDueCount ?? 0
  const totalCount = dueCount + overdueCount + noDueCount
  const showMoreCount = Math.max(totalCount - tasksList.length, 0)

  const hasTasks = overdueCount > 0 || dueCount > 0 || noDueCount > 0

  return {
    title: i18n.t('insights.widgets.tasks.title'),
    todoTasks: formatTasks(tasksList.filter((task) => task.status === 'todo')),
    inProgressTasks: formatTasks(tasksList.filter((task) => task.status === 'in_progress')),
    overdue: {
      label: i18n.t('insights.widgets.tasks.overdue'),
      content: overdueCount.toString(),
    },
    due: {
      label: i18n.t('insights.widgets.tasks.due'),
      content: dueCount.toString(),
    },
    noDue: {
      label: i18n.t('insights.widgets.tasks.no_due'),
      content: noDueCount.toString(),
    },
    buttonLabel:
      showMoreCount && hasTasks
        ? i18n.tp('insights.widgets.tasks.see_more', { count: showMoreCount })
        : undefined,
  }
}

type PreparedData = ReturnType<typeof prepareData>

const TasksWidgetComponent: FC<{
  data: PreparedData
  employeeId: string
  onClickWidget?: () => void
}> = ({
  data: { title, todoTasks, inProgressTasks, overdue, due, noDue, buttonLabel },
  employeeId,
  onClickWidget,
}) => {
  const navigate = useNavigate()

  const { navigation } = useNavigation()

  const linkUrl = navigation.employees.id.tasks.fullPath({
    id: employeeId,
  })

  const handleClickTask = ({ id: taskId }: { id: string | number }) => {
    navigate(
      navigation.tasks.taskId.fullPath({
        taskId,
      })
    )
  }

  useBlocker(({ nextLocation }) => {
    if (linkUrl.endsWith(nextLocation.pathname)) {
      onClickWidget?.()
    }
    return false
  })

  const handleNavigate = () => {
    navigate(linkUrl)
  }

  const link = {
    url: linkUrl,
    title: i18n.t('insights.widgets.tasks.title'),
  }

  const taskCategories = [overdue, due, noDue]

  return (
    <Widget
      header={{
        title,
        link,
      }}
      action={buttonLabel ? { label: buttonLabel, onClick: handleNavigate } : undefined}
    >
      <IndicatorsList items={taskCategories} />
      <Box className={'Tasks_tasksList'}>
        <TasksList
          tasks={{
            inProgress: inProgressTasks,
            todo: todoTasks,
          }}
          onClickTask={handleClickTask}
          emptyMessage={i18n.t('insights.widgets.tasks.empty')}
        />
      </Box>
    </Widget>
  )
}

export const buildTasksWidget = () =>
  buildWidget({
    id: 'tasks',
    document: TasksInsightDocument,
    prepareData,
    component: TasksWidgetComponent,
  })
